/**
 * The conditions line on a rule row, rendered for the active scope.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The source is stated rather than left blank: a scope showing the default
 * looks identical to one deliberately pinned to the same number, and only the
 * badge separates them. "From country" appears when a campaign is selected but
 * only its country carries a value — editing there creates a campaign row.
 */

import type { FC } from 'react';

import { isDefaultScope, type TagRuleScope, type ValueSource } from '@/features/tag-rule-scope';
import type { TagRuleCondition } from '@/hooks';
import { cn } from '@/lib/utils';

import { formatScopedCondition } from '../format-scoped-condition';
import { OverridesBadge } from '../overrides';

const BADGE = 'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide';

const STYLE: Record<ValueSource, { label: string; cls: string; title: string }> = {
  own: {
    label: 'override',
    cls: 'bg-primary/12 text-primary',
    title: 'This scope has its own value for this metric',
  },
  country: {
    label: 'from country',
    cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    title: 'No campaign value — this comes from the country override',
  },
  default: {
    label: 'inherited',
    cls: 'bg-muted text-muted-foreground',
    title: 'Nothing set for this scope — it follows the default',
  },
};

export const ScopedConditionLine: FC<{
  conditions: TagRuleCondition[];
  scope: TagRuleScope | undefined;
  formatBase: (c: TagRuleCondition) => string;
}> = ({ conditions, scope, formatBase }) => (
  <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
    {conditions.map((c, i) => {
      const { text, state } = formatScopedCondition(c, scope, formatBase);
      const badge = state ? STYLE[state] : null;
      return (
        <span key={i} className="inline-flex items-center gap-1.5">
          {i > 0 && <span className="text-muted-foreground/60">AND</span>}
          <span>{text}</span>
          {(!scope || isDefaultScope(scope)) && <OverridesBadge condition={c} />}
          {badge && (
            <span className={cn(BADGE, badge.cls)} title={badge.title}>
              {badge.label}
            </span>
          )}
        </span>
      );
    })}
  </p>
);
